import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { API_CONFIG } from '../../config/api.config';
import { X, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/';

  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 登录表单状态
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  // 注册表单状态
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    nickname: '',
  });

  const [showPassword, setShowPassword] = useState(false);

  // 处理登录
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    // 验证
    if (!loginForm.username.trim()) {
      setError('请输入用户名或邮箱');
      return;
    }
    if (!loginForm.password.trim()) {
      setError('请输入密码');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.USER_LOGIN, {
        username: loginForm.username.trim(),
        password: loginForm.password,
      });

      if (response.code === 0) {
        // 保存 token 和用户信息
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // 刷新页面以更新数据
        window.location.href = redirectPath;
      } else {
        setError(response.message || '登录失败');
      }
    } catch (err) {
      console.error('登录失败:', err);
      setError('登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理注册
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // 验证
    if (!registerForm.username.trim()) {
      setError('请输入用户名');
      return;
    }
    if (registerForm.username.length < 3) {
      setError('用户名至少3个字符');
      return;
    }
    if (!registerForm.email.trim()) {
      setError('请输入邮箱');
      return;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(registerForm.email)) {
      setError('邮箱格式不正确');
      return;
    }
    if (!registerForm.password.trim()) {
      setError('请输入密码');
      return;
    }
    if (registerForm.password.length < 6) {
      setError('密码至少6个字符');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.USER_REGISTER, {
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
        password: registerForm.password,
        nickname: registerForm.nickname.trim() || undefined,
      });

      if (response.code === 0) {
        // 保存 token 和用户信息
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // 刷新页面以更新数据
        window.location.href = redirectPath;
      } else {
        setError(response.message || '注册失败');
      }
    } catch (err) {
      console.error('注册失败:', err);
      setError('注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* 关闭按钮 */}
        <button
          onClick={() => navigate(redirectPath)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {/* 标题 */}
        <div className="p-6 pb-4">
          <h2 className="text-2xl font-bold text-gray-900 text-center">
            {isLogin ? '登录' : '注册'}
          </h2>
        </div>

        {/* 表单 */}
        <div className="px-6 pb-6">
          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {isLogin ? (
            // 登录表单
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label
                  htmlFor="login-username"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  用户名或邮箱
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="login-username"
                    type="text"
                    value={loginForm.username}
                    onChange={(e) => {
                      setLoginForm({ ...loginForm, username: e.target.value });
                      setError('');
                    }}
                    placeholder="请输入用户名或邮箱"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={(e) => {
                      setLoginForm({ ...loginForm, password: e.target.value });
                      setError('');
                    }}
                    placeholder="请输入密码"
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '登录中...' : '登录'}
              </button>
            </form>
          ) : (
            // 注册表单
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label
                  htmlFor="register-username"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  用户名
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="register-username"
                    type="text"
                    value={registerForm.username}
                    onChange={(e) => {
                      setRegisterForm({ ...registerForm, username: e.target.value });
                      setError('');
                    }}
                    placeholder="至少3个字符"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  邮箱
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="register-email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => {
                      setRegisterForm({ ...registerForm, email: e.target.value });
                      setError('');
                    }}
                    placeholder="your@email.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    value={registerForm.password}
                    onChange={(e) => {
                      setRegisterForm({ ...registerForm, password: e.target.value });
                      setError('');
                    }}
                    placeholder="至少6个字符"
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-nickname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  昵称（可选）
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="register-nickname"
                    type="text"
                    value={registerForm.nickname}
                    onChange={(e) => {
                      setRegisterForm({ ...registerForm, nickname: e.target.value });
                      setError('');
                    }}
                    placeholder="你的昵称"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '注册中...' : '注册'}
              </button>
            </form>
          )}

          {/* 切换模式 */}
          <div className="mt-6 text-center text-sm text-gray-600">
            {isLogin ? (
              <span>
                还没有账号？{' '}
                <button
                  onClick={toggleMode}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  立即注册
                </button>
              </span>
            ) : (
              <span>
                已有账号？{' '}
                <button
                  onClick={toggleMode}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  立即登录
                </button>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
